import React, { useCallback, useEffect, useState } from 'react';
import { auth } from '../firebaseConfig';

// Connect Your AI: lets each user connect Resi directly to the AI app they use (via MCP).
// Tokens are created and revoked by the server (/api/mcp-tokens, a Netlify Function) using the
// signed-in user's Firebase login. Only a hash is stored; the token is shown once.
// On localhost the function does not run, so the panel is preview-only there.

const RESI_URL = 'https://resisystem.com/mcp';
const SIGN_IN = 'A Resi sign-in page opens. Type your Resi email and password and tap Sign in. Then tap Approve.';

// Plan and menu names checked against each app's help pages, Nov 2026. Apps change menus often.
const AI_APPS = [
  { key: 'grokbot', label: 'Grok Bot', token: true, note: 'Works on a phone or a computer. The steps are the same.', steps: [
    'Open Grok Bot and start a chat.',
    `Type exactly: "Add a custom MCP server named Resi at ${RESI_URL}" and send it.`,
    'Grok Bot asks if it can add it. Tap Approve (or Yes).',
    'You see a card that says Authorize. Tap it.',
    SIGN_IN,
    'Go back to Grok Bot. It should say Resi is connected.',
    'Test it. Type @, pick Resi, and ask: "What are my tasks today?" You should see your own Resi tasks.',
    'No Authorize card, and Grok Bot asks for a token or key instead? Scroll down on this page, tap Generate token, copy it, and paste it into the secure box Grok Bot shows. Do not paste it into the chat.',
  ] },
  { key: 'grok', label: 'Grok (grok.com)', token: true, note: 'Set it up on a computer at grok.com. On a Grok Business plan, your team admin has to add it first.', steps: [
    'On a computer, go to grok.com/connectors.',
    'Click New Connector.',
    'Click Custom.',
    `Paste this address: ${RESI_URL}`,
    'Click to add it. If it asks you to sign in, do the next step.',
    SIGN_IN,
    'Start a new chat and ask: "Using Resi, what are my tasks today?"',
    'If Grok asks for an API key instead, scroll down on this page, tap Generate token, copy it, and paste it there.',
  ] },
  { key: 'claude', label: 'Claude', oauth: true, note: 'Works on every Claude plan. The free plan allows one custom connector. Set it up on a computer at claude.ai or in the Claude Desktop app. After that it works in the Claude phone app too. On a Claude Team or Enterprise plan, only the owner can add it.', steps: [
    'On a computer, go to claude.ai (or open the Claude Desktop app) and sign in.',
    'Click Customize, then Connectors.',
    'Click Add (or + Add).',
    'Point to Custom, then click Web (on some screens: Add custom connector).',
    'In Name, type: Resi',
    `In the server URL box, paste: ${RESI_URL}`,
    'Click Continue. Leave the sign-in settings Claude picks as they are. Do not type a client ID or secret.',
    'Click Add.',
    'Click Connect next to Resi.',
    SIGN_IN,
    'In a new chat, click + (or the tools button) and make sure Resi is turned on.',
    'Test it. Ask: "Using Resi, what are my tasks today?"',
  ] },
  { key: 'chatgpt', label: 'ChatGPT', oauth: true, note: 'Plan check first. Free and Go plans cannot add Resi. Business plans: only a workspace admin or owner can add it. Pro and Plus may only be able to look things up, not make changes. If yours cannot add it, use Claude or Grok Bot. Set it up on a computer at chatgpt.com. The ChatGPT phone app cannot set it up.', steps: [
    'On a computer, go to chatgpt.com and sign in.',
    'Click Plugins (in the left side menu or in Settings).',
    'Click the + button.',
    'Click Add custom MCP server.',
    'In Name, type: Resi',
    `Under Connection, paste this Server URL: ${RESI_URL}`,
    'For authentication, choose OAuth.',
    'Check the box that says "I understand and want to continue."',
    'Click Create.',
    SIGN_IN,
    'Start a new chat. Type @ and pick Resi.',
    'Test it. Ask: "What are my tasks today?"',
  ] },
  { key: 'gemini', label: 'Gemini', oauth: true, note: 'Only works with a personal Google account (not a work or school one), for adults in the US, in English, with Keep Activity turned on. Google may also require a paid Google AI plan. Set it up on a computer at gemini.google.com. After that it works in the Gemini phone app too.', steps: [
    'On a computer, go to gemini.google.com and sign in with your personal Google account.',
    'At the bottom left, click Settings.',
    'Click Connected Apps. (If you do not see it, click Personal Intelligence first.)',
    'Under Custom apps, click Add a custom app.',
    `Paste: ${RESI_URL}`,
    'Click Next.',
    SIGN_IN,
    'In a chat, type @, pick Resi, and ask: "What are my tasks today?"',
  ] },
  { key: 'perplexity', label: 'Perplexity', token: true, note: 'Set it up on a computer at perplexity.ai. If you are on a company Perplexity account, an admin may have to allow custom connectors first.', steps: [
    'On a computer, go to perplexity.ai and sign in.',
    'In the left side menu, click Plugins. (If you do not see Plugins, click Customize, then Connectors.)',
    'Click Add custom, then Add MCP connector.',
    'Pick Remote.',
    'In Name, type: Resi',
    `In MCP server URL, paste: ${RESI_URL}`,
    'Click Advanced. Set Authentication to OAuth and Transport to Streamable HTTP.',
    'Check the box that says you understand the risk.',
    'Click Add.',
    'Click the Resi card to connect it.',
    SIGN_IN,
    'Test it. Ask: "Using Resi, what are my tasks today?"',
  ] },
  { key: 'other', label: 'Other AI app', token: true, note: 'Any AI app that lets you add a custom MCP connector or server can use Resi.', steps: [
    'In your AI app, find the place to add a connector, plugin, or custom MCP server.',
    'Name it Resi.',
    `Paste the server URL: ${RESI_URL}`,
    'If it offers OAuth or "sign in", choose that. A Resi sign-in page opens. Type your Resi email and password, tap Sign in, then tap Approve.',
    'If it asks for a token, API key, or Bearer token instead, scroll down on this page, tap Generate token, copy it, and paste it there.',
    'Test it. Ask: "Using Resi, what are my tasks today?"',
  ] },
];

const TROUBLESHOOT = [
  'Use your own Resi email and password, the same ones you use on resisystem.com. Not your Google, Grok, or ChatGPT password.',
  'Not sure of your Resi password? Go to resisystem.com, tap Forgot password, set a new one, then try again.',
  `Check the address. It must be exactly ${RESI_URL} with no space and no slash at the end.`,
  'The AI says "Unauthorized" or "please reconnect"? Remove Resi from your AI app and add it again. If you used a token, tap Replace token below and use the new one. Only the newest token works.',
  'Lost your token? It cannot be shown again. Tap Replace token to get a new one.',
  'The Add or New Connector button is missing? Your plan or your company account may not allow custom connectors. Read the plan note above, or try Claude or Grok Bot instead.',
  'You see the wrong person\'s data? You signed in as someone else. Remove Resi from the AI app, sign out of resisystem.com, sign in as yourself, and connect again.',
  'Your Resi account was turned off? The connection stops working. Ask Kelly to turn it back on.',
  'Still stuck? Take a screenshot of what your AI app says and send it to Kelly.',
];

const card = { background: '#ffffff', border: '1px solid #d8c9a8', borderRadius: 10, padding: 20, marginBottom: 16 };
const h2 = { fontSize: 18, fontWeight: 700, color: '#1a1a1a', margin: '0 0 12px' };
const muted = { fontSize: 14, color: '#5B6270', lineHeight: 1.5, margin: '0 0 12px' };
const label = { display: 'block', fontSize: 13, color: '#1a1a1a', fontWeight: 600, marginBottom: 4 };
const select = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 15, marginBottom: 12, background: '#ffffff' };
const btn = { backgroundColor: '#1a1a1a', color: '#D4AF37', border: 'none', padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 15, marginRight: 8 };
const code = { display: 'block', background: '#F6F1E6', border: '1px solid #d8c9a8', borderRadius: 6, padding: '8px 10px', fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', margin: '4px 0 12px' };

const isLocal = () => /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const PROD_ORIGIN = 'https://resisystem.com';

async function api(method, body, query = '') {
  const user = auth.currentUser;
  if (!user) throw new Error('Please sign in again.');
  const idToken = await user.getIdToken();
  const res = await fetch(`/api/mcp-tokens${query}`, {
    method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!(res.headers.get('content-type') || '').includes('application/json')) throw new Error('Token service is not available.');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const err = new Error(data.error || 'Request failed'); err.status = res.status; throw err; }
  return data;
}

export default function ConnectYourAI({ isSelf = true, isAdmin = false }) {
  const [appKey, setAppKey] = useState('');
  const [access, setAccess] = useState('readwrite');
  const [tokens, setTokens] = useState([]);
  const [apps, setApps] = useState([]);
  const [newToken, setNewToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [available, setAvailable] = useState(null); // null = checking, false = token server not reachable
  const preview = available === false;
  const endpoint = `${isLocal() ? PROD_ORIGIN : window.location.origin}/mcp`;
  const app = AI_APPS.find((a) => a.key === appKey);

  const load = useCallback(async () => {
    if (!isSelf) return;
    try { const d = await api('GET'); setTokens(d.tokens || []); setApps(d.apps || []); setAvailable(true); } catch (e) { if (e.status) { setAvailable(true); setMsg(e.message); } else { setAvailable(false); } }
  }, [isSelf]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true); setMsg(null); setNewToken('');
    try {
      if (tokens.some((t) => !t.revoked) && !window.confirm('Replace your current token? The AI app using it will need the new one.')) { setBusy(false); return; }
      const r = await api('POST', { label: app ? app.label : 'AI app', access });
      setNewToken(r.token); await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this token? Your AI app will lose access right away.')) return;
    setBusy(true); setMsg(null);
    try { await api('DELETE', null, `?id=${encodeURIComponent(id)}`); await load(); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this app? It will lose access right away.')) return;
    setBusy(true); setMsg(null);
    try { await api('DELETE', null, `?app=${encodeURIComponent(id)}`); await load(); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <h2 style={h2}>Connect Your AI</h2>
      <p style={muted}>Connect Resi directly to the AI app you use, so you can ask in plain language, like "What is my commission so far this year?" or "Add Rick Winkler as a buyer, condo, 2 bed, 2 bath, $200k to $300k."</p>
      <p style={muted}><strong>What it can reach:</strong> the AI signs in as you and follows your role. {isAdmin ? 'As the admin, you have full read and write access to all Resi data.' : 'Agents can read and change only their own deals, pipeline, leads, tasks and account. Other agents\' data and the admin\'s data are never visible.'} Before any change, your AI should confirm with you.</p>

      <label style={label} htmlFor="cyaApp">Your AI app</label>
      <select id="cyaApp" style={select} value={appKey} onChange={(e) => { setAppKey(e.target.value); setMsg(null); }}>
        <option value="">Choose one</option>
        {AI_APPS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
      </select>

      {app && (
        <>
          <span style={label}>Server URL</span>
          <code style={code}>{endpoint}</code>
          {app.note && <p style={muted}><strong>Before you start:</strong> {app.note}</p>}
          <span style={label}>Setup steps for {app.label}</span>
          <ol style={{ ...muted, paddingLeft: 20 }}>{app.steps.map((s) => <li key={s}>{s}</li>)}</ol>
          {(

            <>
              <span style={label}>If it doesn't work</span>
              <ul style={{ ...muted, paddingLeft: 20 }}>{TROUBLESHOOT.map((s) => <li key={s}>{s}</li>)}</ul>
            </>
          )}
        </>
      )}

      {isSelf && apps.length > 0 && (
        <>
          <span style={label}>Connected apps (signed in with Resi)</span>
          {apps.map((a) => (
            <div key={a.id} style={{ ...muted, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span>{a.clientName} ({(a.scopes || []).includes('resi:write') ? 'read and write' : 'read only'}), connected {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''}</span>
              <button type="button" style={btn} disabled={busy} onClick={() => disconnect(a.id)}>Revoke</button>
            </div>
          ))}
        </>
      )}

      {isSelf && app?.token && (
        <>
          <label style={label} htmlFor="cyaAccess">Access for this token</label>
          <select id="cyaAccess" style={select} value={access} onChange={(e) => setAccess(e.target.value)}>
            <option value="readwrite">Read and write</option>
            <option value="read">Read only</option>
          </select>
          <button type="button" style={{ ...btn, opacity: !available || busy ? 0.5 : 1, cursor: !available || busy ? 'not-allowed' : 'pointer' }} disabled={!available || busy} onClick={create}>{tokens.some((t) => !t.revoked) ? 'Replace token' : 'Generate token'}</button>
          {preview && <p style={{ ...muted, marginTop: 12 }}>{isLocal() ? 'The token service does not run on this test computer. Use resisystem.com instead.' : 'Token service is not available yet. Try again after the next site update.'}</p>}
          {newToken && (
            <>
              <p style={{ ...muted, marginTop: 12 }}><strong>Copy this token now.</strong> It will not be shown again. Any older token stopped working. Paste it into your AI app where it asks for a token or key.</p>
              <code style={code}>{newToken}</code>
            </>
          )}
          {tokens.length > 0 && (
            <>
              <span style={label}>Your tokens</span>
              {tokens.map((t) => (
                <div key={t.id} style={{ ...muted, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span>{t.label || 'AI app'} ending in {t.last4} ({(t.scopes || []).includes('resi:write') ? 'read and write' : 'read only'}){t.revoked ? ', revoked' : ''}</span>
                  {!t.revoked && <button type="button" style={btn} disabled={busy} onClick={() => revoke(t.id)}>Revoke</button>}
                </div>
              ))}
            </>
          )}
        </>
      )}
      {!isSelf && <p style={muted}>Tokens are personal. Each person creates their own from their own Profile.</p>}
      {msg && <p style={{ ...muted, marginTop: 12 }} role="status">{msg}</p>}
    </div>
  );
}
